import { Compass, MapPin, MessageSquare, ReceiptText } from "lucide-react";
import { META } from "@/content/meta";
import { FOUNDERS } from "@/content/founders";
import { SITE } from "@/content/site";
import { usePageMeta } from "@/hooks/usePageMeta";
import { Reveal } from "@/components/motion/Reveal";
import { PageHero } from "@/components/site/PageHero";
import { FinalCTA } from "@/components/site/FinalCTA";
import { PlaceholderTag, SectionIntro } from "@/components/site/Bits";
import { ServiceAreaMap } from "@/components/about/ServiceAreaMap";

const PRINCIPLES = [
  [MessageSquare, "Plain language", "No jargon. We explain every step in everyday terms."],
  [ReceiptText, "Fixed prices", "You'll know the price before any work begins."],
  [Compass, "Design-first", "Good structure makes a site easy to use, just like a good building."],
  [MapPin, "Close by", "We're in Wayne and work with businesses within about 30 miles."],
];

const FounderCard = ({ founder, index }) => (
  <Reveal delay={index * 0.1} data-testid={`founder-card-${founder.id}`}>
    <div className="relative aspect-[4/5] overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0E1015]">
      {founder.photo ? (
        <img src={founder.photo} alt={`${founder.name}, founder of ATA Forge`} className="h-full w-full object-cover" />
      ) : (
        <div role="img" aria-label={`Photo placeholder for ${founder.name}`} className="hatch absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_30%,rgba(47,107,255,0.18),transparent_70%)]" />
          <span className="text-outline absolute inset-0 grid place-items-center text-[9rem] font-semibold leading-none">
            {founder.name[0]}
          </span>
          <span className="absolute bottom-4 left-4">
            <PlaceholderTag testId={`founder-photo-placeholder-${founder.id}`}>Photo placeholder</PlaceholderTag>
          </span>
        </div>
      )}
    </div>
    <div className="mt-5 flex items-baseline justify-between">
      <h3 className="text-xl font-medium tracking-tight">{founder.name}</h3>
      <span className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA]">Founder</span>
    </div>
  </Reveal>
);

export default function About() {
  usePageMeta(META.about);
  return (
    <>
      <PageHero
        eyebrow="About"
        lines={["A local studio", "with an architect's eye."]}
        copy="ATA Forge is a web design and automation studio run by three founders in Wayne, NJ. We build fast, modern websites for the small businesses around us."
        testId="about-hero"
      />
      <section className="border-t border-white/[0.08] py-20 lg:py-28" data-testid="about-story">
        <div className="container-x grid gap-10 lg:grid-cols-12 lg:gap-16">
          <Reveal as="p" className="text-2xl font-medium leading-snug tracking-[-0.02em] text-white sm:text-3xl lg:col-span-5">
            We think about websites the way architects think about buildings.
          </Reveal>
          <Reveal delay={0.1} className="space-y-5 text-base leading-relaxed text-[#A1A1AA] sm:text-lg lg:col-span-7">
            <p>
              Our background is in design and architecture, and we bring it to every project. We plan each site around
              the people using it: what they need to find, where they'll tap, and how quickly they can reach you.
            </p>
            <p>
              We keep the process simple. You get a free mockup first, a fixed price before any work begins, and a
              site that's live in 7–10 days.
            </p>
            <p>
              And because we're local, you're working with neighbors. We know the area, and you can always reach a real
              person.
            </p>
          </Reveal>
        </div>
      </section>
      <section className="section border-t border-white/[0.08]" data-testid="founders-section">
        <div className="container-x">
          <SectionIntro eyebrow="The founders" title="Three founders, one studio." testId="founders-heading" />
          <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {FOUNDERS.map((f, i) => (
              <FounderCard key={f.id} founder={f} index={i} />
            ))}
          </div>
        </div>
      </section>
      <section className="border-t border-white/[0.08] py-20 lg:py-28" data-testid="principles-section">
        <div className="container-x">
          <Reveal className="grid gap-px overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.08] sm:grid-cols-2 lg:grid-cols-4">
            {PRINCIPLES.map(([Icon, title, copy]) => (
              <div key={title} className="bg-[#0A0B0D] p-6 sm:p-8">
                <Icon className="h-5 w-5 text-[#2F6BFF]" aria-hidden="true" />
                <h3 className="mt-6 text-lg font-medium tracking-tight">{title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-[#A1A1AA]">{copy}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>
      <section className="section border-t border-white/[0.08]" data-testid="service-area-section">
        <div className="container-x grid items-center gap-14 lg:grid-cols-2">
          <SectionIntro
            eyebrow="Service area"
            title={["Based in Wayne.", "Working within about 30 miles."]}
            copy="We work with businesses in Wayne, NJ and about 30 miles around, including towns across Passaic, Bergen, Morris, and Essex counties. Not sure if you're in range? Just ask."
            testId="service-area-heading"
          >
            <Reveal delay={0.2} className="mt-8 inline-flex items-center gap-2 text-sm text-white/85" data-testid="about-service-area">
              <MapPin className="h-4 w-4 text-[#2DD4D4]" aria-hidden="true" />
              {SITE.serviceArea}
            </Reveal>
          </SectionIntro>
          <Reveal delay={0.1}>
            <ServiceAreaMap />
            <p className="mt-4 text-center font-mono text-xs text-[#A1A1AA]">Approximate distances</p>
          </Reveal>
        </div>
      </section>
      <FinalCTA />
    </>
  );
}
